import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import {
  doc,
  getDoc,
  getDocFromCache,
  updateDoc,
  collection,
  setDoc,
  deleteDoc,
  getDocs,
  serverTimestamp,
} from 'firebase/firestore';
import './Recipe.css';
import ConfirmationModal from './ConfirmationModal';
import { adjustQuantity as adjustQuantityUtil } from '../utils/recipeUtils';
import useConfirmation from '../hooks/useConfirmation';
import PageHeader from './PageHeader';
import CookingMode from './CookingMode';

// Retourne le document depuis le serveur, depuis le cache si hors-ligne,
// ou un objet "non trouvé" si le document n'est pas en cache.
const safeGetDoc = async ref => {
  try {
    return await getDoc(ref);
  } catch (err) {
    if (err.code === 'unavailable') {
      try {
        return await getDocFromCache(ref);
      } catch {
        return { exists: () => false, data: () => ({}) };
      }
    }
    throw err;
  }
};

function Recipe() {
  const { recipeId } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [servings, setServings] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [successMessage, setSuccessMessage] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [cookingModeActive, setCookingModeActive] = useState(false);

  const { confirmationState, showConfirmation, handleConfirm, handleCancel } =
    useConfirmation();

  // Charger les données de la recette
  useEffect(() => {
    const fetchRecipeData = async () => {
      if (!auth.currentUser) {
        navigate('/');
        return;
      }

      try {
        setLoading(true);
        setError('');

        const userRecipeRef = doc(
          db,
          'recettesUtilisateur',
          auth.currentUser.uid,
          'recettes',
          recipeId
        );
        const userRecipeSnap = await safeGetDoc(userRecipeRef);

        if (!userRecipeSnap.exists()) {
          setError('Recette non trouvée');
          setLoading(false);
          return;
        }

        const recipeData = userRecipeSnap.data();

        // Charger catégories, stats et favoris en parallèle
        const categoryPromises = (recipeData.categories || []).map(categoryId =>
          safeGetDoc(doc(db, 'categoriesUtilisateur', auth.currentUser.uid, 'categories', categoryId))
        );
        const statsRef = doc(db, 'statistiquesRecettes', auth.currentUser.uid, 'recettes', recipeId);
        const favoriteRef = doc(db, 'recettesFavorites', auth.currentUser.uid, 'recettes', recipeId);

        const [categorySnaps, statsSnap, favoriteSnap] = await Promise.all([
          Promise.all(categoryPromises),
          safeGetDoc(statsRef),
          safeGetDoc(favoriteRef),
        ]);

        const categoriesData = [];
        categorySnaps.forEach((categorySnap, index) => {
          if (categorySnap.exists()) {
            categoriesData.push({ id: recipeData.categories[index], ...categorySnap.data() });
          }
        });

        const statsData = statsSnap.exists()
          ? statsSnap.data()
          : { etoiles: 0, nombrePreparations: 0, derniereCuisson: null };

        setIsFavorite(favoriteSnap.exists());

        // Créer l'objet recette complet
        const fullRecipe = {
          id: recipeId,
          ...recipeData,
          categories: categoriesData,
          stats: statsData,
        };

        setRecipe(fullRecipe);
        setServings(fullRecipe.nombrePersonnes || 1);
        setRating(recipeData.note || 0);
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement de la recette:', error);
        setError(
          'Erreur lors du chargement de la recette. Veuillez réessayer.'
        );
        setLoading(false);
      }
    };

    fetchRecipeData();
  }, [recipeId, navigate]);

  // Compter l'utilisation d'une recette dans le planning et la liste de courses
  const countRecipeUsage = async recipeIdToCheck => {
    let planningCount = 0;
    let inShoppingList = false;

    try {
      // Compter les créneaux dans le planning
      const planningSnapshot = await getDocs(
        collection(db, 'planningRepas', auth.currentUser.uid, 'plans')
      );
      planningSnapshot.forEach(planDoc => {
        const repas = planDoc.data().repas || {};
        Object.values(repas).forEach(meal => {
          if (meal && meal.idRecette === recipeIdToCheck) planningCount++;
        });
      });

      // Vérifier si la recette est référencée dans la liste de courses active
      const shoppingSnapshot = await getDocs(
        collection(db, 'listesCourses', auth.currentUser.uid, 'listes')
      );
      shoppingSnapshot.forEach(listDoc => {
        const data = listDoc.data();
        if (data.dateCompletion === null || data.dateCompletion === undefined) {
          const items = data.articles || [];
          if (items.some(item => item.recipeIds && item.recipeIds.includes(recipeIdToCheck))) {
            inShoppingList = true;
          }
        }
      });
    } catch (error) {
      console.warn('Erreur lors du comptage des usages:', error);
    }

    return { planningCount, inShoppingList };
  };

  // Nettoyer la référence d'une recette dans la liste de courses active
  const cleanupRecipeFromShoppingList = async recipeIdToRemove => {
    try {
      const shoppingSnapshot = await getDocs(
        collection(db, 'listesCourses', auth.currentUser.uid, 'listes')
      );
      const updatePromises = [];

      shoppingSnapshot.forEach(listDoc => {
        const data = listDoc.data();
        if (data.dateCompletion === null || data.dateCompletion === undefined) {
          const articles = data.articles || [];
          const updatedArticles = articles.map(item => {
            if (item.recipeIds && item.recipeIds.includes(recipeIdToRemove)) {
              return {
                ...item,
                recipeIds: item.recipeIds.filter(id => id !== recipeIdToRemove),
              };
            }
            return item;
          });
          updatePromises.push(
            updateDoc(listDoc.ref, { articles: updatedArticles })
          );
        }
      });

      if (updatePromises.length > 0) await Promise.all(updatePromises);
    } catch (error) {
      console.warn('Erreur lors du nettoyage de la liste de courses:', error);
    }
  };

  // Fonction pour nettoyer les références dans les plannings
  const cleanupRecipeFromPlanning = async recipeIdToRemove => {
    try {
      const planningCollection = collection(
        db,
        'planningRepas',
        auth.currentUser.uid,
        'plans'
      );

      // Récupérer tous les plans
      const planningSnapshot = await getDocs(planningCollection);

      const updatePromises = [];

      planningSnapshot.forEach(planDoc => {
        const planData = planDoc.data();
        let needsUpdate = false;
        const updatedMeals = { ...planData.repas };

        // Vérifier chaque type de repas
        Object.keys(updatedMeals).forEach(mealType => {
          if (
            updatedMeals[mealType] &&
            updatedMeals[mealType].idRecette === recipeIdToRemove
          ) {
            delete updatedMeals[mealType];
            needsUpdate = true;
          }
        });

        // Si le plan a été modifié, ajouter à la liste des mises à jour
        if (needsUpdate) {
          if (Object.keys(updatedMeals).length === 0) {
            // Si plus aucun repas, supprimer le plan entier
            updatePromises.push(deleteDoc(planDoc.ref));
          } else {
            // Sinon, mettre à jour les repas
            updatePromises.push(
              updateDoc(planDoc.ref, {
                repas: updatedMeals,
                dateMiseAJour: serverTimestamp(),
              })
            );
          }
        }
      });

      // Exécuter toutes les mises à jour
      if (updatePromises.length > 0) {
        await Promise.all(updatePromises);
      }
    } catch (error) {
      console.warn('Erreur lors du nettoyage des plannings:', error);
      // Ne pas faire échouer la suppression pour cette erreur
    }
  };

  // Fonction pour ajouter/supprimer des favoris
  const toggleFavorite = async () => {
    try {
      const favoriteRef = doc(
        db,
        'recettesFavorites',
        auth.currentUser.uid,
        'recettes',
        recipeId
      );

      if (isFavorite) {
        // Supprimer des favoris
        await deleteDoc(favoriteRef);
        setMessage({ type: 'success', text: 'Recette retirée des favoris' });
      } else {
        // Ajouter aux favoris
        await setDoc(favoriteRef, {
          dateAjout: serverTimestamp(),
          idRecette: recipeId,
          etoiles: recipe.stats.etoiles || 0,
        });
        setMessage({ type: 'success', text: 'Recette ajoutée aux favoris' });
      }

      setIsFavorite(!isFavorite);

      // Effacer le message après 3 secondes
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
    } catch (error) {
      console.error('Erreur lors de la mise à jour des favoris:', error);
      setMessage({
        type: 'error',
        text: 'Erreur lors de la mise à jour des favoris',
      });
    }
  };

  // Fonction pour naviguer vers la page d'édition
  const handleEdit = () => {
    navigate(`/edit-recipe/${recipeId}`);
  };

  // Fonction pour supprimer la recette
  const handleDelete = async () => {
    if (!recipe) return;

    // Compter l'utilisation avant d'afficher la modale
    const { planningCount, inShoppingList } = await countRecipeUsage(recipeId);

    const recipeDetails = (
      <div>
        <div style={{ marginBottom: '15px' }}>
          <strong>Recette à supprimer :</strong> <strong>{recipe.nom}</strong>
          {recipe.ingredients && recipe.ingredients.length > 0 && (
            <div style={{ marginTop: '6px', color: 'var(--text-secondary)', fontSize: '14px' }}>
              {recipe.ingredients.length} ingrédient{recipe.ingredients.length > 1 ? 's' : ''} •{' '}
              {recipe.etapesPreparation?.length || 0} étape{(recipe.etapesPreparation?.length || 0) > 1 ? 's' : ''}
            </div>
          )}
        </div>

        <div style={{
          backgroundColor: 'var(--bg-tertiary)',
          padding: '12px',
          borderRadius: '6px',
          border: '1px solid var(--warning-color)',
          fontSize: '14px',
        }}>
          <strong>⚠️ Conséquences de la suppression :</strong>
          <ul style={{ margin: '8px 0 0 20px', padding: 0, lineHeight: '1.8' }}>
            <li>La recette et ses statistiques seront supprimées</li>
            {isFavorite && <li>Retirée de vos favoris</li>}
            {planningCount > 0 && (
              <li>
                <strong style={{ color: 'var(--error-color)' }}>
                  {planningCount} créneau{planningCount > 1 ? 'x' : ''} du planning {planningCount > 1 ? 'seront supprimés' : 'sera supprimé'}
                </strong>
              </li>
            )}
            {inShoppingList && (
              <li>
                <strong style={{ color: 'var(--warning-color)' }}>
                  Référence retirée de votre liste de courses active
                </strong>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                  Les articles déjà cochés ou fusionnés ne seront pas modifiés.
                </div>
              </li>
            )}
          </ul>
          <div style={{ marginTop: '10px', fontWeight: 'bold', color: 'var(--error-color)' }}>
            Cette action est irréversible.
          </div>
        </div>
      </div>
    );

    showConfirmation({
      title: 'Supprimer cette recette',
      message: 'Vous êtes sur le point de supprimer définitivement cette recette de votre bibliothèque.',
      details: recipeDetails,
      confirmText: 'Supprimer définitivement',
      cancelText: 'Annuler',
      type: 'danger',
      onConfirm: async () => {
        try {
          setLoading(true);

          // Supprimer la recette principale
          await deleteDoc(doc(db, 'recettesUtilisateur', auth.currentUser.uid, 'recettes', recipeId));

          // Supprimer les statistiques
          try {
            await deleteDoc(doc(db, 'statistiquesRecettes', auth.currentUser.uid, 'recettes', recipeId));
          } catch (e) { /* déjà absentes */ }

          // Supprimer des favoris si présent
          if (isFavorite) {
            try {
              await deleteDoc(doc(db, 'recettesFavorites', auth.currentUser.uid, 'recettes', recipeId));
            } catch (e) { /* déjà absent */ }
          }

          // Nettoyer le planning
          await cleanupRecipeFromPlanning(recipeId);

          // Nettoyer la liste de courses
          await cleanupRecipeFromShoppingList(recipeId);

          setSuccessMessage(`Recette "${recipe.nom}" supprimée avec succès`);
          setTimeout(() => navigate('/recipes'), 2000);
        } catch (error) {
          console.error('Erreur lors de la suppression de la recette:', error);
          setMessage({ type: 'error', text: 'Erreur lors de la suppression de la recette. Veuillez réessayer.' });
          setLoading(false);
        }
      },
    });
  };

  // Mettre à jour la note de la recette
  const handleRating = async (stars) => {
    const newRating = rating === stars ? 0 : stars;
    setRating(newRating);
    try {
      const recipeRef = doc(db, 'recettesUtilisateur', auth.currentUser.uid, 'recettes', recipeId);
      await updateDoc(recipeRef, { note: newRating });
    } catch (error) {
      console.error('Erreur lors de la notation:', error);
      setRating(rating);
    }
  };

  // Fonction pour ajouter au planning
  const handleAddToPlanning = () => {
    sessionStorage.setItem(
      'selectedRecipe',
      JSON.stringify({
        recipeId: recipeId,
        servings: servings,
      })
    );
    navigate('/meal-planner');
  };

  // Fonction pour ajouter à la liste de courses
  const handleAddToShoppingList = () => {
    sessionStorage.setItem(
      'addToShoppingList',
      JSON.stringify({
        recipeId: recipeId,
        servings: servings,
      })
    );
    navigate('/shopping-list');
  };

  // Fonction pour calculer le temps total
  const getTotalTime = () => {
    if (!recipe) return 0;
    return (recipe.tempsPreparation || 0) + (recipe.tempsCuisson || 0);
  };

  const adjustQuantity = quantity =>
    adjustQuantityUtil(quantity, servings, recipe.nombrePersonnes);

  if (loading) {
    return <div className="loading">Chargement de la recette...</div>;
  }

  if (error) {
    return (
      <div className="recipe-error">
        <h2>Erreur</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/recipes')}>
          Retour à la liste des recettes
        </button>
      </div>
    );
  }

  if (!recipe) {
    return (
      <div className="recipe-error">
        <h2>Recette non trouvée</h2>
        <button onClick={() => navigate('/recipes')}>
          Retour à la liste des recettes
        </button>
      </div>
    );
  }

  return (
    <div className="recipe-container">
      {message.text && (
        <div className={`message ${message.type}`}>{message.text}</div>
      )}

      {successMessage && (
        <div className="message success">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>✅</span>
            <div>
              <strong>{successMessage}</strong>
              <br />
              <small>Redirection vers la liste des recettes...</small>
            </div>
          </div>
        </div>
      )}

      <PageHeader
        title={recipe.nom}
        backTo="/recipes"
        backAriaLabel="Retour à la liste des recettes"
        sticky={true}
        actions={
          <>
            <button
              className={`favorite-button ${isFavorite ? 'active' : ''}`}
              onClick={toggleFavorite}
              title={isFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
            >
              <svg width="20" height="20" viewBox="0 0 24 24"
                fill={isFavorite ? 'currentColor' : 'none'}
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
            </button>
            <button
              className="edit-button"
              onClick={handleEdit}
              title="Modifier la recette"
            >
              <svg width="18" height="18" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
            </button>
            <button
              className="delete-button"
              onClick={handleDelete}
              title="Supprimer la recette"
            >
              <svg width="18" height="18" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
              </svg>
            </button>
          </>
        }
      />

      {/* Contenu principal */}
      <div className="recipe-main-content">
        {/* Catégories */}
        {recipe.categories && recipe.categories.length > 0 && (
          <div className="recipe-categories">
            {recipe.categories.map(category => (
              <span
                key={category.id}
                className="category-tag"
                style={{
                  backgroundColor: `${category.couleur}20`,
                  color: category.couleur,
                  borderColor: category.couleur,
                }}
              >
                {category.nom}
              </span>
            ))}
          </div>
        )}

        {/* Note personnelle */}
        <div className="recipe-rating-section">
          <span className="recipe-rating-label">Ma note</span>
          <div className="recipe-rating-stars">
            {[1, 2, 3, 4, 5].map(star => (
              <button
                key={star}
                className={`star-btn ${star <= (hoverRating || rating) ? 'star-filled' : 'star-empty'}`}
                onClick={() => handleRating(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                aria-label={`${star} étoile${star > 1 ? 's' : ''}`}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
              </button>
            ))}
          </div>
          {rating > 0 && <span className="recipe-rating-value">{rating}/5</span>}
        </div>

        <div className="recipe-meta">
          {recipe.tempsPreparation > 0 && (
            <div className="meta-item">
              <span className="meta-icon">⏱️</span>
              <div className="meta-content">
                <span className="meta-label">Préparation</span>
                <span className="meta-value">{recipe.tempsPreparation} min</span>
              </div>
            </div>
          )}
          {recipe.tempsCuisson > 0 && (
            <div className="meta-item">
              <span className="meta-icon">🔥</span>
              <div className="meta-content">
                <span className="meta-label">Cuisson</span>
                <span className="meta-value">{recipe.tempsCuisson} min</span>
              </div>
            </div>
          )}
          {getTotalTime() > 0 && (
            <div className="meta-item">
              <span className="meta-icon">⏳</span>
              <div className="meta-content">
                <span className="meta-label">Total</span>
                <span className="meta-value">{getTotalTime()} min</span>
              </div>
            </div>
          )}
          {recipe.difficulte && (
            <div className="meta-item">
              <span className="meta-icon">🍽️</span>
              <div className="meta-content">
                <span className="meta-label">Difficulté</span>
                <span className="meta-value">{recipe.difficulte}</span>
              </div>
            </div>
          )}
          {recipe.stats.nombrePreparations > 0 && (
            <div className="meta-item">
              <span className="meta-icon">📊</span>
              <div className="meta-content">
                <span className="meta-label">Planifiée</span>
                <span className="meta-value">{recipe.stats.nombrePreparations} fois</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Barre d'action */}
      <div className="recipe-action-bar">

        {/* Mode cuisine — action principale */}
        {recipe.etapesPreparation && recipe.etapesPreparation.length > 0 && (
          <button
            className="action-button cooking-mode-btn"
            onClick={() => setCookingModeActive(true)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/>
              <path d="M7 2v20"/>
              <path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3zm0 0v7"/>
            </svg>
            Mode cuisine
          </button>
        )}

        {/* Groupe : portions + ajout planning/courses */}
        <div className="action-add-group">
          <div className="servings-selector">
            <span>Portions</span>
            <button onClick={() => setServings(Math.max(1, servings - 1))} disabled={servings <= 1}>−</button>
            <span className="servings-value">{servings}</span>
            <button onClick={() => setServings(servings + 1)}>+</button>
          </div>
          <div className="action-add-buttons">
            <button className="action-button primary" onClick={handleAddToPlanning}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              <span className="btn-label-full">Ajouter au planning</span>
              <span className="btn-label-short">Planning</span>
            </button>
            <button className="action-button primary" onClick={handleAddToShoppingList}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
                <line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/>
              </svg>
              <span className="btn-label-full">Ajouter à la liste de courses</span>
              <span className="btn-label-short">Liste de courses</span>
            </button>
          </div>
        </div>

      </div>

      {recipe.description && (
        <div className="recipe-description">
          <p>{recipe.description}</p>
          {recipe.source && (
            <p className="recipe-source">Source: {recipe.source}</p>
          )}
        </div>
      )}

      <div className="recipe-section ingredients">
        <h2>Ingrédients</h2>

        {recipe.ingredients && recipe.ingredients.length > 0 ? (
          <ul className="ingredients-list">
            {recipe.ingredients.map((ingredient, index) => {
              const baseOrange = '#ff7043';
              const orangeLight = 'rgba(255, 112, 67, 0.1)';

              const itemStyle = {
                '--ingredient-color': baseOrange,
                '--ingredient-bg-light': orangeLight,
              };

              return (
                <li key={index} className="ingredient-item" style={itemStyle}>
                  <div className="ingredient-quantity">
                    {adjustQuantity(ingredient.quantite)} {ingredient.unite}
                  </div>
                  <div className="ingredient-details">
                    <span className="ingredient-name">{ingredient.nom}</span>
                    {ingredient.commentaire && (
                      <span className="ingredient-comment">
                        ({ingredient.commentaire})
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="empty-message">Aucun ingrédient enregistré</p>
        )}
      </div>

      <div className="recipe-section steps">
        <h2>Préparation</h2>
        {recipe.etapesPreparation && recipe.etapesPreparation.length > 0 ? (
          <ol className="steps-list">
            {recipe.etapesPreparation.map(step => (
              <li key={step.numero} className="step-item">
                <div className="step-number">{step.numero}</div>
                <div className="step-content">
                  <p>{step.description}</p>
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <p className="empty-message">
            Aucune étape de préparation enregistrée
          </p>
        )}
      </div>

      {/* Mode cuisine */}
      {cookingModeActive && (
        <CookingMode
          recipe={recipe}
          onClose={() => setCookingModeActive(false)}
        />
      )}

      {/* Modal de confirmation pour la suppression */}
      <ConfirmationModal
        isOpen={confirmationState.isOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        title={confirmationState.title}
        message={confirmationState.message}
        confirmText={confirmationState.confirmText}
        cancelText={confirmationState.cancelText}
        type={confirmationState.type}
        details={confirmationState.details}
      />
    </div>
  );
}

export default Recipe;
